import { styled } from '@linaria/react'
import { font } from '@reapit/elements/utils/font'

/** The recipient canvas — same full-width sizing as the agent canvas, for consistent card scale. */
export const WizardColumn = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: 1rem;
`

/** One step block in the canvas: just groups the divider + step card, no styling of its own. */
export const StepBlock = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 1rem;
`

/** The step itself, boxed white — mirrors the agent view's outer card (SectionTile) so the same
 * white-card → grey-card → white-field layering shows up on both sides of the builder. */
export const StepCard = styled.div`
  background-color: white;
  border: 1px solid var(--neutral-100, #e5e9ed);
  border-radius: 0.75rem;
  box-shadow: 0 1px 2px rgba(18, 43, 51, 0.06);
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  row-gap: 1rem;
`

export const StepProgress = styled.div`
  ${font('sm', 'regular')}
  color: var(--colour-text-secondary, #607890);
`

export const StepProgressCurrent = styled.span`
  color: var(--colour-text-action, #4e56ea);
  font-weight: 500;
`

export const StepTitle = styled.h2`
  ${font('xl', 'bold')}
  color: var(--colour-text-primary, #222b33);
  margin: 0.125rem 0 0;
`

export const StepDescription = styled.p`
  ${font('sm', 'regular')}
  color: var(--colour-text-secondary, #607890);
  margin: 0;
`

/** A recipient card as the recipient sees it: header + the Heading / Main / Additional regions. Grey,
 * nested inside the white StepCard — mirrors the agent view's SectionBlock nested inside its SectionTile. */
export const RecipientCardBox = styled.div`
  background-color: #e9edf2;
  border: 1px solid var(--neutral-100, #e5e9ed);
  border-radius: 0.5rem;
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  row-gap: 0.75rem;
`

export const RecipientCardHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 0.625rem;
`

export const RecipientCardName = styled.span`
  ${font('sm', 'regular')}
  color: var(--colour-text-secondary, #607890);
`

/** Marks a card that repeats (AllowMultiple) — e.g. one Method of Payment card per payment method. */
export const RepeatBadge = styled.span`
  ${font('xs', 'regular')}
  margin-left: auto;
  padding: 0.0625rem 0.5rem;
  border-radius: 1rem;
  border: 1px solid var(--neutral-150, #d8dee4);
  background-color: var(--colour-fill-neutral-lightest, #f2f4f6);
  color: var(--colour-text-secondary, #607890);
`

/** Builder-only caption naming a card region (the recipient never sees these labels). */
export const RegionCaption = styled.div`
  ${font('xs', 'regular')}
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--colour-text-secondary, #8c99a8);
  border-bottom: 1px solid var(--neutral-100, #e5e9ed);
  padding-bottom: 0.1875rem;
  margin-bottom: 0.375rem;
`

/** Wraps multiple Heading-region rows (e.g. a heading built from several linked fields on one line) with
 * the same rhythm as AdditionalBlock — HeadingValue itself has no margin, so without this they'd render
 * flush against each other. */
export const HeadingBlock = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 0.375rem;
`

/** Heading-region value — larger/bold, like the person name at the top of a recipient card. Boxed white,
 * nested inside the grey RecipientCardBox — mirrors the agent view's white field inputs inside a grey section. */
export const HeadingValue = styled.div`
  ${font('base', 'bold')}
  color: var(--colour-text-primary, #222b33);
  background-color: white;
  border: 1px solid var(--neutral-150, #d8dee4);
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 0.5rem;
`

/** A Main-region row: label on the left, value slot on the right. Boxed white, like an input control. */
export const FieldRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 0.5rem;
  background-color: white;
  border: 1px solid var(--neutral-150, #d8dee4);
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
`

export const FieldLabel = styled.span`
  ${font('sm', 'regular')}
  color: var(--colour-text-secondary, #607890);
  min-width: 130px;
`

/** Pushes a row's Edit/Delete affordances to the right-hand edge of a field row. */
export const InlineRowActions = styled.div`
  margin-left: auto;
  display: flex;
  flex-direction: row;
  align-items: center;
`

/** The Additional region: its FieldRows are already boxed white, so this just stacks them with a divider
 * from the body above (no background of its own — the card underneath it is already the grey layer). */
export const AdditionalBlock = styled.div`
  border-top: 1px dashed var(--neutral-200, #c3ccd5);
  padding-top: 0.625rem;
  display: flex;
  flex-direction: column;
  row-gap: 0.375rem;
`

/** A collapsed summary for a run of empty preview values — used instead of stacking N identical
 * placeholder rows, since the factory preview always builds against an empty placeholder recipient. */
export const FactoryEmptyNote = styled.div`
  ${font('sm', 'regular')}
  font-style: italic;
  color: var(--colour-text-secondary, #8c99a8);
  background-color: white;
  border: 1px dashed var(--neutral-150, #d8dee4);
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
`

/** A dynamic value slot showing its data source (merge token / linked field) rather than real data. */
export const SourceTag = styled.span`
  ${font('sm', 'regular')}
  font-family: var(--font-mono, monospace);
  color: var(--colour-text-action, #185fa5);
  background-color: var(--colour-fill-action-lightest, #e6f1fb);
  padding: 0.0625rem 0.4375rem;
  border-radius: 5px;
`

/** A literal value the recipient always sees (fixed default data, no link/merge). */
export const LiteralValue = styled.span`
  ${font('sm', 'regular')}
  color: var(--colour-text-primary, #222b33);
`

/** An action field (signature etc.) rendered as its affordance rather than a value. */
export const ActionSlot = styled.div`
  ${font('sm', 'regular')}
  border: 1px dashed var(--neutral-200, #c3ccd5);
  border-radius: 0.375rem;
  padding: 1.125rem;
  text-align: center;
  color: var(--colour-text-secondary, #607890);
`

/** Non-functional Back / Continue nav, shown so the developer sees the step's shape. In the real recipient
 * app these sit in the top nav bar, not a footer — mirrored here at the top of the step card. */
export const WizardNav = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  opacity: 0.5;
  pointer-events: none;
`

export const StepDivider = styled.div`
  border-top: 1px dashed var(--neutral-200, #c3ccd5);
  margin: 1.5rem 0 0.5rem;
`

/** Caption on a component-factory-driven card, matching the agent view's "Component-generated" note. */
export const FactoryCardCaption = styled.div`
  ${font('xs', 'regular')}
  color: var(--colour-text-secondary, #8c99a8);
`
